/* =========================
   bundle-builder.js (FULL)
   ========================= */

class BundleBuilder extends HTMLElement {
  connectedCallback() {
    this.currency = this.dataset.currency || 'USD';
    this.bundlePrice = parseInt(this.dataset.bundlePrice || '0', 10);
    this.comparePrice = parseInt(this.dataset.comparePrice || '0', 10);
    this.discountPercent = parseInt(this.dataset.discountPercent || '0', 10);

    const productsRaw = this.querySelector('script[data-bundle-products]')?.textContent || '[]';
    this.itemsCfg = JSON.parse(productsRaw);

    this.$items = this.querySelector('[data-items]');
    this.$btn = this.querySelector('[data-add-bundle]');
    this.$total = this.querySelector('[data-bundle-total]');

    this.state = {
      products: new Map(),
      selections: new Map(),
      gpoOptionSet: new Map()
    };

    this.init();
  }

  escapeHtml(str) {
    return String(str ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  formatMoney(cents) {
    const format = this.$total?.dataset.moneyFormat;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && format) {
      return window.Shopify.formatMoney(cents, format);
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency
    }).format((cents || 0) / 100);
  }

  async init() {
    for (const it of this.itemsCfg) {
      this.state.selections.set(it.handle, { variantId: null, qty: 1 });
    }

    this.bind();

    await Promise.all(this.itemsCfg.map(async (it) => {
      try {
        const res = await fetch('/products/' + it.handle + '.js');
        if (!res.ok) return;
        const p = await res.json();
        this.state.products.set(it.handle, p);
        const firstAvail = p.variants.find(function(v) { return v.available; }) || p.variants[0];
        const sel = this.state.selections.get(it.handle);
        if (sel) sel.variantId = firstAvail ? firstAvail.id : null;
        this.updateItemInDom(it.handle);
      } catch (e) {
        console.warn('BundleBuilder: urun yuklenemedi:', it.handle, e);
      }
    }));

    this.updateTotals();

    await Promise.all(this.itemsCfg.map(async (it) => {
      try {
        const optionSetId = await this.fetchGpoOptionSetId(it.handle);
        if (optionSetId) this.state.gpoOptionSet.set(it.handle, optionSetId);
      } catch (e) {
        console.warn('BundleBuilder: GPO yuklenemedi:', it.handle, e);
      }
    }));

    if (this.state.gpoOptionSet.size > 0) {
      this.render();
      this.mountGpoPlaceholders();
      this.rebootGpoOnce();
      this.updateTotals();
    }
  }

  updateItemInDom(handle) {
    const p = this.state.products.get(handle);
    const sel = this.state.selections.get(handle);
    if (!p) return;

    const row = this.$items.querySelector('.bundle-item[data-handle="' + handle + '"]');
    if (!row) return;

    const currentImg = row.querySelector('.bundle-item__img');
    const imgSrc = p.featured_image || (p.images && p.images[0]) || '';
    if (currentImg && imgSrc && currentImg.tagName !== 'IMG') {
      const img = document.createElement('img');
      img.className = 'bundle-item__img';
      img.src = imgSrc;
      img.alt = this.escapeHtml(p.title || handle);
      currentImg.replaceWith(img);
    }

    const priceEl = row.querySelector('[data-item-price]');
    if (priceEl && sel && sel.variantId) {
      const v = this.getVariantById(handle, sel.variantId);
      if (v) priceEl.textContent = this.formatMoney(v.price);
    }
  }

  async fetchGpoOptionSetId(handle) {
    try {
      const res = await fetch('/products/' + handle, { credentials: 'same-origin' });
      if (!res.ok) return '';
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const gpo = doc.querySelector('.gpo-app[data-option-set-id]');
      const id = gpo ? (gpo.getAttribute('data-option-set-id') || '') : '';
      return id.trim();
    } catch (e) {
      console.warn('fetchGpoOptionSetId failed', handle, e);
      return '';
    }
  }

  render() {
    const self = this;
    const html = this.itemsCfg.map(function(it) {
      const p = self.state.products.get(it.handle);
      const sel = self.state.selections.get(it.handle);
      const title = it.title || (p && p.title) || it.handle;
      const img = (p && p.featured_image) || (p && p.images && p.images[0]) || '';
      const url = '/products/' + it.handle;
      const v = (sel && sel.variantId) ? self.getVariantById(it.handle, sel.variantId) : null;
      const priceText = v ? self.formatMoney(v.price) : '\u2014';
      const optionSetId = self.state.gpoOptionSet.get(it.handle);
      const hasGpo = !!optionSetId;
      const imgHtml = img
        ? '<img class="bundle-item__img" src="' + img + '" alt="' + self.escapeHtml(title) + '">'
        : '<div class="bundle-item__img" aria-hidden="true"></div>';
      const gpoHtml = hasGpo
        ? '<div class="bundle-gpo" data-gpo-wrap data-option-set-id="' + optionSetId + '"></div>'
        : '';
      const qtyVal = (sel && sel.qty != null) ? sel.qty : 1;
      return '<div class="bundle-item" data-handle="' + it.handle + '">'
        + '<a class="bundle-item__link" href="' + url + '" aria-label="' + self.escapeHtml(title) + '">'
        + imgHtml
        + '<div class="bundle-item__info">'
        + '<div class="bundle-item__title">' + self.escapeHtml(title) + '</div>'
        + '<div class="bundle-item__price" data-item-price>' + priceText + '</div>'
        + gpoHtml
        + '</div>'
        + '</a>'
        + '<div class="bundle-item__controls">'
        + '<div class="bundle-qty">'
        + '<button type="button" data-dec aria-label="Decrease">\u2212</button>'
        + '<input type="number" min="0" value="' + qtyVal + '" data-qty />'
        + '<button type="button" data-inc aria-label="Increase">+</button>'
        + '</div>'
        + '</div>'
        + '</div>';
    }).join('');
    this.$items.innerHTML = html;
  }

  mountGpoPlaceholders() {
    const wraps = this.$items.querySelectorAll('[data-gpo-wrap]');
    wraps.forEach(function(wrap) {
      const optionSetId = wrap.getAttribute('data-option-set-id');
      const bundleItem = wrap.closest('.bundle-item');
      const handle = bundleItem ? (bundleItem.dataset.handle || '') : '';
      wrap.innerHTML = '<form class="gpo-proxy-form" data-gpo-proxy-form data-handle="' + handle + '">'
        + '<input type="hidden" name="id" value="">'
        + '<div class="gpo-product-variants"></div>'
        + '<div class="gpo-container">'
        + '<div class="gpo-app" data-option-set-id="' + optionSetId + '"></div>'
        + '</div>'
        + '</form>';
      // No access to 'this' here via forEach — use the outer instance
    });

    // Set variant IDs on hidden inputs
    const self = this;
    wraps.forEach(function(wrap) {
      const bundleItem = wrap.closest('.bundle-item');
      const handle = bundleItem ? (bundleItem.dataset.handle || '') : '';
      const sel = self.state.selections.get(handle);
      const hidden = wrap.querySelector('input[name="id"]');
      if (hidden && sel && sel.variantId) hidden.value = String(sel.variantId);
    });
  }

  rebootGpoOnce() {
    const any = this.$items.querySelector('.gpo-app[data-option-set-id]');
    if (!any) return;
    if (window.__BUNDLE_GPO_REBOOTED__) return;
    window.__BUNDLE_GPO_REBOOTED__ = true;
    const existing = Array.from(document.scripts).find(function(s) {
      return s.src && s.src.includes('globo-product-options') && s.src.includes('gpomain.js');
    });
    if (!existing || !existing.src) {
      console.warn('gpomain.js bulunamadi.');
      return;
    }
    const s = document.createElement('script');
    s.src = existing.src.split('?')[0] + '?v=' + Date.now();
    s.defer = true;
    document.head.appendChild(s);
  }

  getVariantById(handle, variantId) {
    const p = this.state.products.get(handle);
    if (!p || !p.variants) return null;
    return p.variants.find(function(v) { return Number(v.id) === Number(variantId); }) || null;
  }

  updateTotals() {
    // Secili urunlerin toplam fiyati (product.price'dan bagimsiz)
    let originalTotal = 0;
    for (const it of this.itemsCfg) {
      const sel = this.state.selections.get(it.handle);
      if (!sel || !sel.variantId) continue;
      if (Number(sel.qty) <= 0) continue;
      const v = this.getVariantById(it.handle, sel.variantId);
      if (!v) continue;
      originalTotal += Number(v.price) * Number(sel.qty);
    }

    // Secili urun sayisi
    let selectedCount = 0;
    this.state.selections.forEach(function(s) {
      if (Number(s.qty) > 0) selectedCount++;
    });
    const countEl = this.querySelector('[data-bundle-item-count]');
    if (countEl) countEl.textContent = '(' + selectedCount + ')';

    // Indirim gecerliligi: tum urunler sette olmali (hicbiri qty=0 olmamali)
    let allSelected = true;
    for (const it of this.itemsCfg) {
      const sel = this.state.selections.get(it.handle);
      if (!sel || Number(sel.qty) <= 0) { allSelected = false; break; }
    }
    const discountActive = this.discountPercent > 0 && allSelected;

    // Son fiyat: urunlerin toplamina indirim uygula (product.price yok sayilir)
    const finalPrice = discountActive
      ? Math.round(originalTotal * (100 - this.discountPercent) / 100)
      : originalTotal;

    if (this.$total) this.$total.textContent = this.formatMoney(finalPrice);

    // Alt toplam: indirim aktifse ustü cizili toplam goster
    const itemsOrigEl = this.querySelector('[data-bundle-items-original]');
    if (itemsOrigEl) {
      if (discountActive && originalTotal > 0) {
        itemsOrigEl.textContent = this.formatMoney(originalTotal);
        itemsOrigEl.style.display = '';
      } else {
        itemsOrigEl.style.display = 'none';
      }
    }

    // Indirim kodu rozeti: indirim aktif degilse gizle
    const badgeEl = this.querySelector('[data-bundle-badge]');
    if (badgeEl) badgeEl.style.display = discountActive ? '' : 'none';

    // Set Fiyati satiri: SADECE gercek compare_at_price varsa goster (JS'te statik birakiyoruz)
    // comparePrice liquid'den geliyor, product.price'dan bagimsiz
    const setRow = this.querySelector('[data-price-set-row]');
    if (setRow) {
      if (this.comparePrice > 0 && this.comparePrice > this.bundlePrice) {
        setRow.style.display = '';
      } else {
        setRow.style.display = 'none';
      }
    }

    // Ana sayfa fiyat sync
    const mainPriceEl = document.querySelector(
      'body.template-product--set-urunler product-price .price'
    ) || document.querySelector(
      'body.template-product-set-urunler product-price .price'
    );
    if (mainPriceEl && !this.contains(mainPriceEl)) {
      mainPriceEl.textContent = this.formatMoney(finalPrice);
    }
  }

  bind() {
    const self = this;

    this.$items.addEventListener('change', function(e) {
      const row = e.target.closest('.bundle-item');
      if (!row) return;
      const handle = row.dataset.handle;
      const sel = self.state.selections.get(handle);
      if (!sel) return;
      if (e.target.matches('[data-qty]')) {
        sel.qty = Math.max(0, Number(e.target.value || 0));
        row.style.opacity = sel.qty === 0 ? '0.5' : '1';
        self.updateTotals();
      }
    });

    this.$items.addEventListener('click', function(e) {
      if (e.target.closest('.bundle-qty')) {
        e.preventDefault();
        e.stopPropagation();
      }
      const row = e.target.closest('.bundle-item');
      if (!row) return;
      const handle = row.dataset.handle;
      const sel = self.state.selections.get(handle);
      if (!sel) return;
      const qtyInput = row.querySelector('[data-qty]');
      if (e.target.matches('[data-inc]')) {
        sel.qty = (Number(sel.qty) || 0) + 1;
        if (qtyInput) qtyInput.value = sel.qty;
        row.style.opacity = '1';
        self.updateTotals();
      }
      if (e.target.matches('[data-dec]')) {
        sel.qty = Math.max(0, (Number(sel.qty) || 0) - 1);
        if (qtyInput) qtyInput.value = sel.qty;
        row.style.opacity = sel.qty === 0 ? '0.5' : '1';
        self.updateTotals();
      }
    });

    this.$items.addEventListener('click', function(e) {
      const gpoArea = e.target.closest('.bundle-gpo, .gpo-container, .gpo-app');
      if (gpoArea) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    if (this.$btn) this.$btn.addEventListener('click', function() { self.addToCart(); });
  }

  collectGpoPropertiesFromRow(row) {
    const props = {};
    const inputs = row.querySelectorAll(
      '.gpo-container [data-name^="properties["], .gpo-container [name^="properties["]'
    );
    inputs.forEach(function(el) {
      const type = (el.getAttribute('type') || '').toLowerCase();
      if ((type === 'radio' || type === 'checkbox') && !el.checked) return;
      const rawName = el.getAttribute('data-name') || el.getAttribute('name') || '';
      const key = rawName.replace(/^properties\[/, '').replace(/\]$/, '').trim();
      if (!key) return;
      const val = (el.value || '').trim();
      if (!val) return;
      props[key] = val;
    });
    return props;
  }

  async addToCart() {
    const items = [];
    for (const it of this.itemsCfg) {
      const sel = this.state.selections.get(it.handle);
      if (!sel || !sel.variantId) continue;
      if (Number(sel.qty) <= 0) continue;
      const row = this.$items.querySelector('.bundle-item[data-handle="' + it.handle + '"]');
      const props = row ? this.collectGpoPropertiesFromRow(row) : {};
      items.push({ id: Number(sel.variantId), quantity: Number(sel.qty), properties: props });
    }
    if (!items.length) return;
    this.$btn.disabled = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      });
      if (!res.ok) throw new Error('cart/add failed');
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (e) {
      console.error(e);
      alert('Sepete eklenirken hata olustu.');
    } finally {
      this.$btn.disabled = false;
    }
  }
}

customElements.define('bundle-builder', BundleBuilder);
